import React from 'react';
import moment from 'moment-timezone';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';
import * as Sentry from '@sentry/react';
import { message } from 'antd';

import DateTimeWithZone from '../../common/text/DateTimeWithZone';

import { constructFormPayloadForAPI } from '../../forms/ResponderHelpers';
import {
  getIdMap, isNullOrUndefined, sortByCode, uuid, toTitleCase,
} from '../../helpers/helpers';
import { sortByObjProperty } from '../../common/helpers';
import { constructCompletedFormPDF, decorateFormWithFiles } from '../../forms/formHelpers';
import Permissions from '../../auth/Permissions';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';

import {
  getTaskDurationText,
} from '../../helpers/tasks';

const axiosFileInstance = axios.create();

export default {};

export const UNPHASED = 'Unphased';

export const unphasedCode = { id: UNPHASED, name: UNPHASED };

const formatTaskBase = (task = {}) => {
  const {
    date,
    startTime,
    originalStart,
    originalEnd,
    endTime,
    costcodeId,
    costcode,
    projectId,
    project,
    phase,
    phaseId,
    type,
    note,
    classId,
    sageShiftId,
    hourBased,
    userId,
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
    excludeFromAutoBreak,
    isNightShift,
    groupId,
  } = task;

  const {
    id: costcodeObjectId = costcodeId,
  } = costcode || {};

  const {
    id: projectObjectId = projectId,
  } = project || {};

  const {
    id: phaseObjectId = phaseId,
  } = phase || {};
  
  return {
    type,
    note: note || null,
    phaseId: phaseObjectId === UNPHASED || !phaseObjectId ? null : phaseObjectId,
    costcodeId: costcodeObjectId ?? null,
    projectId: projectObjectId ?? null,
    classId: classId ? classId : undefined, // eslint-disable-line
    sageShiftId: sageShiftId ? sageShiftId : undefined, // eslint-disable-line
    startTime,
    endTime,
    originalStart,
    originalEnd,
    hourBased: !!hourBased,
    date,
    userId,
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
    excludeFromAutoBreak: !!excludeFromAutoBreak,
    isNightShift: !!isNightShift,
    groupId,
  };
};

const prepareTasksForUpload = async ({ tasks = [], addId }) => {
  const newTasks = [];
  let fullFiles = [];
  await Promise.all(
    tasks.map(async (task = {}) => {
      const {
        id,
        customData,
        divisionId,
        timezone,
      } = task;

      const missingClockInOrOut = !TaskHelpers.getStartTime(task) || !TaskHelpers.getEndTime(task);
      if (missingClockInOrOut) return;

      let newTask = formatTaskBase(task);

      if (timezone) newTask.timezone = timezone; // Copy will have timezone
      if (addId) newTask.id = id;
      else {
        newTask = TaskHelpers.decorateTaskWithDate({
          dateTimestamp: TaskHelpers.getEndTime(task),
          payload: newTask,
        });
      }

      if (customData && !Array.isArray(customData)) {
        const {
          data: {
            sections,
          } = {},
          files,
        } = await constructFormPayloadForAPI({
          form: {
            collected: {}, responses: customData,
          },
        });
        newTask.customData = sections;
        newTask.divisionId = divisionId;
        fullFiles = fullFiles.concat(files);
      }

      newTasks.push(newTask);
    }),
  );

  return { tasks: newTasks, files: fullFiles };
};

export const prepareManualEntryPayload = async (rawTask = {}) => {
  const {
    tasksToAdd: entriesToAdd = [],
    tasksToEdit: entriesToEdit = [],
    tasksToRemove: entriesToRemove = [],
  } = rawTask;

  const {
    tasks: tasksToAdd,
    files: addFiles = [],
  } = await prepareTasksForUpload({
    tasks: entriesToAdd,
  });

  const {
    tasks: tasksToEdit,
    files: editFiles = [],
  } = await prepareTasksForUpload({
    tasks: entriesToEdit,
    addId: true,
  });

  const tasksToRemove = entriesToRemove?.map((task) => ({
    id: task.id,
  })) ?? [];

  return {
    tasksToAdd,
    tasksToEdit,
    tasksToRemove,
    files: addFiles.concat(editFiles),
  };
};

export const zoneToLocalTS = (timestamp, timezone) => {
  if (!timezone || timezone === DateTime.local().zoneName) {
    return timestamp;
  }
  const zoneDT = DateTime.fromMillis(timestamp, { zone: timezone });
  const localDt = DateTime.fromObject(zoneDT.toObject());
  return localDt.toMillis();
};

export const localToZoneTS = (timestamp, timezone) => {
  if (!timezone || timezone === DateTime.local().zoneName) {
    return timestamp;
  }
  const localDT = DateTime.fromMillis(timestamp);
  const zoneDT = DateTime.fromObject({ ...localDT.toObject(), zone: timezone });
  return zoneDT.toMillis();
};

export const dateValidator = ({
  form,
  hourBased,
  isOnlyPTO,
  hasFutureDayPermission,
}) => async (_, value) => {
  if (!value || typeof value !== 'string') return Promise.reject(new Error('Date is required'));
  if (
    moment(value).isAfter(moment().endOf('day'))
    && !isOnlyPTO
    && !hasFutureDayPermission
  ) return Promise.reject(new Error('Can\'t create a task in the future'));
  if (!hourBased) {
    // Need to revalidate times to see if they are in the future
    try {
      const {
        startTime,
        endTime,
      } = form.getFieldValues();
      if (!isNullOrUndefined(startTime) && !isNullOrUndefined(endTime)) {
        await form.validateFields(['startTime', 'endTime']);
      }
    } catch (err) {
      // Handled by antd form
    }
  }
  return Promise.resolve();
};

export const ptoTimesValidator = async (_, value) => {
  if (!value) {
    return Promise.reject(new Error('PTO time is required'));
  }

  return Promise.resolve();
};

// Shifts an entry by given offset to maintain ordering
const getShiftedHourBasedEntry = (entry, offset) => {
  const newEntry = { ...entry };
  const {
    startTime,
    endTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    breakStartTime,
    breakEndTime,
    ptoStartTime,
    ptoEndTime,
  } = newEntry;

  if (startTime && endTime) {
    const duration = entry.endTime - entry.startTime;
    newEntry.startTime = startTime + offset;
    newEntry.endTime = startTime + duration + offset;
  }

  if (otStartTime && otEndTime) {
    const duration = otEndTime - otStartTime;
    newEntry.otStartTime = otStartTime + offset;
    newEntry.otEndTime = otStartTime + duration + offset;
  }

  if (doubleOTStartTime && doubleOTEndTime) {
    const duration = doubleOTEndTime - doubleOTStartTime;
    newEntry.doubleOTStartTime = doubleOTStartTime + offset;
    newEntry.doubleOTEndTime = doubleOTStartTime + duration + offset;
  }

  if (breakStartTime && breakEndTime) {
    const duration = breakEndTime - breakStartTime;
    newEntry.breakStartTime = breakStartTime + offset;
    newEntry.breakEndTime = breakStartTime + duration + offset;
  }

  if (ptoStartTime && ptoEndTime) {
    const duration = ptoEndTime - ptoStartTime;
    newEntry.ptoStartTime = ptoStartTime + offset;
    newEntry.ptoEndTime = ptoStartTime + duration + offset;
  }

  return newEntry;
};

export const addOrUpdateEntries = ({
  entries = [], // Entries in the days tasks
  newEntry = {}, // Entry being added/modified
  selectedEntry, // The task selected for editing. Does not exist when adding a new task.
  userId,
}) => {
  let formattedEntries;
  let newId;
  if (!selectedEntry || selectedEntry?.userId !== userId) {
    newId = uuid();
    const fullEntry = {
      ...newEntry, id: newId,
    };
    let offset = -1;
    formattedEntries = entries.concat([fullEntry]).map((entry) => {
      if (!fullEntry.hourBased) return { ...entry, userId };
      const taskDate = TaskHelpers.getTaskDate(entry)?.toSQLDate();
      if (entry.hourBased
        && taskDate === fullEntry.date) {
        offset += 1;
      }
      if (entry.id === fullEntry.id) {
        // reorder
        return {
          ...getShiftedHourBasedEntry(entry, offset),
          isUpdated: true,
          userId,
        };
      }
      return entry;
    });
  } else {
    formattedEntries = entries.map((entry) => {
      if (entry.id !== selectedEntry.id) return entry;
      return {
        ...selectedEntry,
        ...newEntry,
        isUpdated: true,
        userId,
      };
    });
  }

  formattedEntries.sort((a, b) => a.startTimeStamp - b.startTimeStamp);
  return { formattedEntries, newId };
};

export const getTimeText = ({
  hourBased,
  type,
  startTime,
  endTime,
  originalStart,
  originalEnd,
  timezone,
}, {
  showAbbreviatedDuration = true,
  returnComponent = true,
  addTypeAsSuffix = false,
  showOriginalTimes = false,
  showZeroTimes = false,
} = {}) => {
  if (
    (startTime === endTime && !showZeroTimes)
    || (isNullOrUndefined(startTime) && isNullOrUndefined(endTime))
  ) return null;

  const prefix = !addTypeAsSuffix && type ? `${type}: ` : '';
  let suffix = addTypeAsSuffix && type ? ` (${type})` : '';

  const durationText = getTaskDurationText({
    startTime,
    endTime,
  }, { showAbbreviatedDuration, showZeroTimes });

  if (!hourBased) {
    if (isNullOrUndefined(endTime) && isNullOrUndefined(startTime)) return null;

    if (!returnComponent) {
      // Only display timezone if there is no end time
      const realStart = TaskHelpers.getDisplayStart({
        startTime, originalStart,
      }, showOriginalTimes);
      const realEnd = TaskHelpers.getDisplayEnd({
        endTime, originalEnd,
      }, showOriginalTimes);
      const startFormat = !isNullOrUndefined(realEnd) ? 'hh:mm a' : 'hh:mm a ZZZZ';
      const startStr = !isNullOrUndefined(realStart)
        ? DateTime.fromMillis(realStart, { zone: timezone }).toFormat(startFormat)
        : '';
      const endStr = !isNullOrUndefined(realEnd)
        ? DateTime.fromMillis(realEnd, { zone: timezone }).toFormat('hh:mm a ZZZZ')
        : '';
      const connector = startStr && endStr ? ' -> ' : '';

      if (showOriginalTimes && durationText) suffix += ` ${durationText}`;

      return `${prefix}${startStr}${connector}${endStr}${suffix}`;
    }
    return (
      <DateTimeWithZone
        type={type}
        startTime={startTime}
        endTime={endTime}
        originalStart={originalStart}
        originalEnd={originalEnd}
        timezone={timezone}
        style={{ marginBottom: 0 }}
        addTypeAsSuffix={addTypeAsSuffix}
        addDurationToTimeBased={showOriginalTimes}
      />
    );
  }

  if (!durationText) return null;

  const text = `${returnComponent ? '' : prefix}${durationText}${suffix}`;

  if (returnComponent) {
    return (
      <>
        <span className="bold-text">{prefix}</span>
        {text}
      </>
    );
  }

  return text;
};

const getTypeText = (type) => {
  switch (type) {
    case 'break': return 'Break';
    case 'overtime': return 'Overtime';
    default: return 'Regular';
  }
};

export const getAllTimesText = ({
  hourBased,
  startTime,
  endTime,
  timezone,
  breakStartTime,
  breakEndTime,
  otStartTime,
  otEndTime,
  doubleOTStartTime,
  doubleOTEndTime,
  ptoStartTime,
  ptoEndTime,
  type,
  originalStart,
  originalEnd,
}, {
  showOriginalTimes = false,
  showAbbreviatedDuration = true,
  returnComponent = true,
  joinText = '\n',
  showRegularTypeText = true,
  addTypeAsSuffix = false,
  showZeroTimes = false,
} = {}) => {
  const results = [];

  const timeText = getTimeText({
    type: showRegularTypeText ? getTypeText(type) : null,
    hourBased,
    startTime: startTime || null,
    endTime: endTime || null,
    originalStart,
    originalEnd,
    timezone,
  }, {
    showAbbreviatedDuration, returnComponent, addTypeAsSuffix, showOriginalTimes, showZeroTimes,
  });

  if (timeText) results.push(timeText);

  const breakText = getTimeText({
    type: 'Break',
    hourBased,
    startTime: breakStartTime || null,
    endTime: breakEndTime || null,
    timezone,
  }, {
    showAbbreviatedDuration, returnComponent, addTypeAsSuffix,
  });

  if (breakText) results.push(breakText);

  const otText = getTimeText({
    type: 'Overtime',
    hourBased,
    startTime: otStartTime || null,
    endTime: otEndTime || null,
    timezone,
  }, {
    showAbbreviatedDuration, returnComponent, addTypeAsSuffix,
  });

  if (otText) results.push(otText);

  const doubleOTText = getTimeText({
    type: 'Double Overtime',
    hourBased,
    startTime: doubleOTStartTime || null,
    endTime: doubleOTEndTime || null,
    timezone,
  }, {
    showAbbreviatedDuration, returnComponent, addTypeAsSuffix,
  });

  if (doubleOTText) results.push(doubleOTText);

  const ptoText = getTimeText({
    type: 'PTO',
    hourBased,
    startTime: ptoStartTime || null,
    endTime: ptoEndTime || null,
    timezone,
  }, {
    showAbbreviatedDuration, returnComponent, addTypeAsSuffix, showZeroTimes,
  });

  if (ptoText) results.push(ptoText);

  if (!results.length) {
    return '';
  }

  if (returnComponent) {
    return (
      <>
        {results.map((result, index) => (
          <>
            {result}
            {(index < results.length - 1) && <br />}
          </>
        ))}
      </>
    );
  }

  return results.join(joinText);
};

export const TextRow = function TextRow({ title, text }) {
  return (
    <span>
      <b>
        {title}
        :
      </b>
      {' '}
      {text}
    </span>
  );
};

export const getName = (id, idMap = {}) => {
  const { [id]: { name } = {} } = idMap;
  return name;
};

export const getCostcodeName = ({
  costcodeId,
  costcodeIdMap,
}) => {
  if (!(costcodeId in costcodeIdMap)) return null;
  const { [costcodeId]: { name, code } = {} } = costcodeIdMap;
  return `${code} - ${name}`;
};

export const getProjectRowNames = ({
  projectId,
  phaseId,
  costcodeId,
  projects,
  phases,
  costcodeIdMap,
}) => {
  const projectIdMap = getIdMap(projects);
  const phaseIdMap = getIdMap(phases);
  const project = projectIdMap[projectId];
  const ourName = getName(phaseId, phaseIdMap);
  const phaseName = !ourName && projectId && costcodeId ? 'Unphased' : ourName;
  const costcodeName = getCostcodeName({
    costcodeId,
    costcodeIdMap,
  });
  return {
    project,
    projectName: `${project?.number} - ${project?.name}`,
    phaseName,
    costcodeName,
  };
};

export const loadManualEntryData = async (taskIds, includeFiles) => {
  const {
    data: {
      dataMap = {},
      fileMap = {},
    } = {},
  } = await axios.get('/tasks/data', { params: { taskIds } });

  if (includeFiles) {
    await decorateFormWithFiles({ fileMap });
  }

  return {
    dataMap,
    fileMap,
  };
};

export const loadTasks = async ({
  startTime,
  endTime,
  startDate,
  endDate,
  userId,
  userIds,
  projectId,
  costcodeName,
  costcodeCode,
}) => {
  try {
    const payload = {};
    if (startDate && endDate) {
      payload.startDate = startDate;
      payload.endDate = endDate;
    } else {
      payload.startTime = startTime;
      payload.endTime = endTime;
      payload.timezone = DateTime.local().zoneName;
    }
    if (userId) payload.userId = userId;
    if (userIds?.length) payload.userIds = userIds;
    if (projectId) payload.projectId = projectId;
    if (costcodeName && costcodeCode) {
      payload.costcodeName = costcodeName;
      payload.costcodeCode = costcodeCode;
    }
    const { data: tasks = [] } = await axios.post('/tasks/get', payload);
    return tasks;
  } catch (err) {
    Sentry.captureException(err);
    const msg = 'Tasks could not be retrieved for the date(s). Conflicts, Auto-break, and Auto-rounding may be affected';
    message.error(msg);
    return [];
  }
};

const constructTimecardPDFHeader = (entry, t, timeText) => {
  /*
    Time based entries have a timezone suffix,
    which causes the line to wrap so
    we add another line to the title
    so the subsequent rows line up
  */
  let timeSuffix = entry.hourBased ? '' : '\n';
  // Need to keep the same number of lines
  const timeLines = timeText.split('\n');
  if (timeLines.length > 1) {
    timeLines.forEach(() => {
      timeSuffix += '\n';
    });
  }
  const additionalCollectedHeaderItems = [{
    title: `Time${timeSuffix}`,
    response: timeText,
    link: {
      collectedId: 'time',
    },
  }];

  if (entry.projectId) {
    additionalCollectedHeaderItems.push({
      title: t('Project'),
      response: entry.projectName,
      link: {
        collectedId: 'project',
      },
    });
  }

  if (entry.phase) {
    additionalCollectedHeaderItems.push({
      title: 'Phase',
      response: entry.phase,
      link: {
        collectedId: 'phase',
      },
    });
  }

  if (entry.fullCostcode) {
    additionalCollectedHeaderItems.push({
      title: 'Cost Code',
      response: entry.fullCostcode,
      link: {
        collectedId: 'costcode',
      },
    });
  }

  if (entry.className) {
    // Needs newline or Classification gets split with hyphen
    additionalCollectedHeaderItems.push({
      title: 'Work\nClassification',
      response: entry.className,
      link: {
        collectedId: 'className',
      },
    });
  }

  if (entry.startLatitude && entry.startLongitude) {
    additionalCollectedHeaderItems.push({
      title: 'Start Location',
      response: `${entry.startLatitude}, ${entry.startLongitude}`,
      link: {
        collectedId: 'startLocation',
      },
    });
  }

  if (entry.endLatitude && entry.endLongitude) {
    additionalCollectedHeaderItems.push({
      title: 'End Location',
      response: `${entry.endLatitude}, ${entry.endLongitude}`,
      link: {
        collectedId: 'endLocation',
      },
    });
  }

  return additionalCollectedHeaderItems;
};

export const constructTimecardPDF = async (processedEntries, data, t) => {
  const {
    employeeId,
    name,
    settings,
    logo,
    fileMap,
    projectIdMap,
    equipmentIdMap,
  } = data;
  let signatureData;

  const { showOriginalTimes } = settings ?? {};

  const signedTask = processedEntries.find((entry) => entry.signatureReferenceId);
  if (signedTask) {
    try {
      const { data: signedURL } = await axios.get(`/signatures/${signedTask.userId}/${signedTask.signatureReferenceId}`);
      const {
        data: signature,
      } = await axiosFileInstance.get(signedURL, {
        responseType: 'arraybuffer',
      });
      signatureData = new File([signature], 'signature.png', { type: 'image' });
    } catch {
      // No op
    }
  }

  const pdfs = await Promise.all(processedEntries.map(async (entry, index) => {
    const timeText = getAllTimesText(
      entry,
      { showAbbreviatedDuration: false, returnComponent: false, showOriginalTimes },
    );
    const additionalCollectedHeaderItems = constructTimecardPDFHeader(entry, t, timeText);
    const date = TaskHelpers.getTaskDate(entry) ?? DateTime.local();
    return constructCompletedFormPDF({
      employeeSignature: signatureData,
      useStandardTemplate: true,
      pdfVersion: 2,
      data: {
        sections: entry.customData,
      },
      formData: entry.formattedCustomData,
      fileMap,
      createdAt: date?.toMillis(),
      submitterName: name,
      employeeId,
      title: `Time Card for ${name}: ${date.toLocaleString(DateTime.DATE_MED)}`,
      settings: {
        ...settings,
        timezone: entry.timezone ?? settings.timezone,
        // ontraccr-form-to-pdf uses this to convert to a date text
      },
      projectIdMap,
      equipmentIdMap,
      logo,
      drawOptions: [],
      shouldReturnPDF: true,
      additionalCollectedHeaderItems,
      collected: {
        employeeSignature: {
          // Only append the signature to the last page
          collect: signatureData && index === processedEntries.length - 1,
        },
        time: timeText,
        project: formatProjectLabelFromCompanySettings({
          name: projectIdMap[entry.projectId]?.name,
          number: projectIdMap[entry.projectId]?.number,
          settings,
        }),
        phase: entry.phase,
        costcode: entry.fullCostcode,
        startLocation: `${entry.startLatitude?.toFixed(2)}, ${entry.startLongitude?.toFixed(2)}`,
        endLocation: `${entry.endLatitude?.toFixed(2)}, ${entry.endLongitude?.toFixed(2)}`,
        className: entry.className,
      },
    });
  }));

  // Merge pdfs
  const mergedPDF = await PDFDocument.create();
  const pages = await Promise.all(pdfs.map(async (pdf) => (
    mergedPDF.copyPages(pdf, pdf.getPageIndices())
  )));

  // Add pages to merged pdf
  // We need to flatten the array of arrays to ensure order is maintained
  pages.forEach((copiedPages) => {
    copiedPages.forEach((page) => mergedPDF.addPage(page));
  });

  return mergedPDF.save();
};

export const getCCLabel = (cc) => `${cc.code} - ${cc.name}`;
export const getOpt = (item) => ({ label: item.name, value: item.id });

export const getProjectPhases = ({
  costcodes,
  phases,
  projectId,
}) => {
  const ccIdMap = getIdMap(costcodes);
  const unphasedCC = costcodes
    .filter(
      (cc) => cc.projectId === projectId
        && (cc.phased === false || cc.phased === 0),
    );
  const relevantPhases = phases
    .filter((phase) => phase.projectId === projectId);

  const phaseMap = {};
  if (unphasedCC.length > 0) {
    phaseMap[UNPHASED] = {
      id: UNPHASED,
      name: UNPHASED,
      costcodes: unphasedCC,
    };
  }
  relevantPhases.forEach((phase) => {
    const newPhase = { ...phase };
    delete newPhase.costcodeId;
    if (!(phase.id in phaseMap)) {
      phaseMap[phase.id] = {
        ...newPhase,
        costcodes: [],
      };
    }
    if (phase.costcodeId && phase.costcodeId in ccIdMap) {
      phaseMap[phase.id].costcodes.push(ccIdMap[phase.costcodeId]);
    }
  });
  return {
    phases: Object.values(phaseMap),
    phaseMap,
  };
};

export const getUserTeamProjects = ({
  user,
  teams,
  projectId,
}) => {
  const userTeamIds = new Set(user.teams?.map((team) => team.id));
  let projectsAcc = user.projects ?? [];
  teams.forEach((team) => {
    if (userTeamIds.has(team.id)) projectsAcc = [...projectsAcc, ...team.projects];
  });

  const projectSet = new Set(projectsAcc.map((project) => project.id));

  if (!projectSet.has(projectId)) projectSet.add(projectId);

  return projectSet;
};

export const getProjectOptions = ({
  activeProjects,
  teams,
  user = {},
  divisionIds = null,
  divisionId,
  projectId,
  settings,
}) => {
  const restrictProjectSelection = Permissions.has('RESTRICT_PROJECT_SELECTION') && Permissions.id === user.id;

  let divisionList = divisionIds ?? [];
  if (divisionId) {
    divisionList = [divisionId];
  }
  const divisionSet = new Set(divisionList);

  const projectOptions = activeProjects
    .filter((project) => (!divisionList.length || divisionSet.has(project.divisionId)))
    .map((project) => ({
      label: formatProjectLabelFromCompanySettings({ ...project, settings }),
      value: project.id,
      name: project.name,
      number: project.number,
    }))
    .sort(sortByObjProperty('label'));

  if (!restrictProjectSelection || !teams) return projectOptions;

  const userTeamProjects = getUserTeamProjects({
    user,
    teams,
    projectId,
  });

  const filteredProjects = projectOptions.filter((project) => (
    userTeamProjects.has(project.value)
  ));

  return filteredProjects;
};

export const getLocalOptions = ({
  localId,
  localIdMap = {},
}) => {
  const {
    [localId]: { unionId: ourUnionId } = {},
  } = localIdMap;
  return Object.values(localIdMap).filter((local) => local.unionId === ourUnionId)
    .map(getOpt);
};

export const getClassOptions = ({
  localId,
  classIdMap = {},
}) => Object.values(classIdMap).filter((unionClass) => unionClass.localId === localId)
  .map(getOpt);

export const getPhaseOptions = ({
  activeCostcodes,
  activePhases,
  projectId,
}) => {
  if (!projectId) return [];

  const {
    phases: parsedPhases,
  } = getProjectPhases({
    costcodes: activeCostcodes,
    phases: activePhases,
    projectId,
  });

  return parsedPhases.map(getOpt);
};

export const getCostcodeOptions = ({
  activeCostcodes,
  activePhases,
  projectId,
  phaseId,
  divisionId,
}) => {
  let relevantCostcodes = [];

  const {
    phaseMap: phaseIdMap,
  } = getProjectPhases({
    costcodes: activeCostcodes,
    phases: activePhases,
    projectId,
  });

  if (!projectId) {
    relevantCostcodes = activeCostcodes.filter((cc) => (
      !cc.projectId && (!divisionId || cc.divisionId === divisionId)
    ));
  } else {
    const {
      [phaseId]: {
        costcodes: phaseCC = [],
      } = {},
    } = phaseIdMap;

    relevantCostcodes = phaseCC;
  }

  return relevantCostcodes
    .filter((cc) => cc.canClockIn)
    .sort(sortByCode('code'))
    .map((cc) => ({ label: getCCLabel(cc), value: cc.id }));
};

export const getUserOptions = ({
  user,
  users,
  divisionId,
  userDivisions,
}) => {
  if (!divisionId) return [{ value: user.id, label: user.name }];

  const usersWithManualEntryPerms = users.filter((userObj) => (
    Permissions.has(`MANUAL_ENTRY_${Permissions.formatPosition(userObj.position)}`)
    && !!userDivisions?.[userObj.id]?.find((divId) => divId === divisionId)
    && userObj.active
  ));

  return usersWithManualEntryPerms.map((userObj) => ({ label: userObj.name, value: userObj.id }));
};

export const convertFromTimeBasedToHourBased = (values) => {
  const {
    timezone = DateTime.local().zoneName,
    date,
    startTime,
    endTime,
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
  } = values;
  if (!date) return values;
  const newValues = { ...values };

  const startOfDay = DateTime.fromISO(date, { zone: timezone }).startOf('day').toMillis();
  if (startTime && endTime) {
    newValues.startTime = startOfDay;
    newValues.endTime = startOfDay + (endTime - startTime);
  }

  if (breakStartTime && breakEndTime) {
    newValues.breakStartTime = startOfDay;
    newValues.breakEndTime = startOfDay + (breakEndTime - breakStartTime);
  }

  if (otStartTime && otEndTime) {
    newValues.otStartTime = startOfDay;
    newValues.otEndTime = startOfDay + (otEndTime - otStartTime);
  }

  if (doubleOTStartTime && doubleOTEndTime) {
    newValues.doubleOTStartTime = startOfDay;
    newValues.doubleOTEndTime = startOfDay + (doubleOTEndTime - doubleOTStartTime);
  }

  if (ptoStartTime && ptoEndTime) {
    newValues.ptoStartTime = startOfDay;
    newValues.ptoEndTime = startOfDay + (ptoEndTime - ptoStartTime);
  }

  return newValues;
};

export const foundTaskConflict = ({
  editIds,
  currentTasks,
  newTasks,
  phases = [],
  projectIdMap = {},
  userMap = {},
  costcodeIdMap = {},
  showOriginalTimes,
  detectHourBasedConflict,
  fromTimeTable = false,
  conflictingTask,
}) => {
  let conflictMessage;

  const isConflicted = conflictingTask ?? TaskHelpers.hasTaskConflict({
    editIds,
    currentTasks,
    newTasks,
    showOriginalTimes,
    detectHourBasedConflict,
  });

  let phaseName = 'Unphased';
  if (isConflicted.phaseId) {
    phaseName = phases.find(({ id }) => id === isConflicted.phaseId)?.name ?? 'Unphased';
  }

  if (isConflicted) {
    const project = projectIdMap[isConflicted.projectId];
    const conflictedUser = userMap?.[isConflicted?.userId];
    const projectTitle = project
      ? `${project.name}-${project.number}`
      : 'None';
    const costcode = costcodeIdMap[isConflicted.costcodeId];
    const costcodeTitle = costcode ? `${costcode.code} - ${costcode.name}` : 'None';
    const date = TaskHelpers.getTaskDate(isConflicted);
    const timeText = getTimeText(isConflicted, { returnComponent: false });
    conflictMessage = (
      <p>
        Time conflict with existing entry:
        <br />
        <br />
        {`Date: ${date.toSQLDate()}`}
        <br />
        {`User: ${conflictedUser?.name}`}
        <br />
        {timeText}
        <br />
        {`Project: ${projectTitle}`}
        <br />
        {`Costcode: ${costcodeTitle}`}
        <br />
        {`Phase: ${phaseName}`}
        <br />
        {`Type: ${toTitleCase(isConflicted.type)}`}
        <br />
        {fromTimeTable && (
          'If this entry was added to the time entry table, the entries will be amended to resolve this conflict.'
        )}
      </p>
    );
  }
  return conflictMessage;
};

export const foundTaskConflictForTimeEntryTable = ({
  tableEntries,
  showOriginalTimes,
  newEntry,
}) => {
  let conflictMessage;
  const isConflicted = TaskHelpers.hasTimeEntryTableOverlap({
    tableEntries,
    showOriginalTimes,
    newEntry,
  });
  const timeText = getTimeText(isConflicted, { returnComponent: false });
  if (isConflicted) {
    conflictMessage = (
      <p>
        Time conflict with existing entry:
        <br />
        <br />
        {timeText}
        <br />
        {`Type: ${toTitleCase(isConflicted.type)}`}
      </p>
    );
  }
  return conflictMessage;
};

export const convertMillisHoursToMillisDate = (ms, date) => {
  if (!date?.isValid) return null;

  if (!ms) return null;

  const time = TaskHelpers.convertMillisecondsToHours(ms);

  const hours = Math.floor(time);
  const minutes = (time * 60) % 60;

  return date.set({ hours, minutes }).toMillis();
};

export const stripSecondsAndMilliseconds = (timestamp) => {
  if (!timestamp) return timestamp;
  const dateTime = DateTime.fromMillis(timestamp).set({ second: 0, millisecond: 0 });
  return dateTime.toMillis();
};

const calculateStartTime = (endTime, timezone) => {
  const parsedEnd = DateTime.fromMillis(endTime, { zone: timezone });
  return parsedEnd.minus({
    hours: parsedEnd.hour,
    minutes: parsedEnd.minute,
  }).toMillis();
};

// Gets the appropriate matching start time for given end time (for hour based)
// Since hour based tasks only have an end time form field
const getTimes = ({
  startTime,
  endTime,
  startTimeKey,
  endTimeKey,
  isTimeShown = true,
  allowZeroTime = false,
  timezone,
}) => {
  if (!isTimeShown) {
    return {
      [startTimeKey]: null,
      [endTimeKey]: null,
    };
  }

  if (startTime) {
    return {
      [startTimeKey]: startTime,
      [endTimeKey]: endTime,
    };
  }

  if (endTime === 0) {
    return {
      [startTimeKey]: 0,
      [endTimeKey]: 0,
    };
  }

  let newStartTime = null;
  let newEndTime = endTime;
  if (!isNullOrUndefined(endTime)) newStartTime = calculateStartTime(endTime, timezone);

  // If hour and minutes is 0, we don't need to set a start time
  // End time could also equal start time for non hour based
  // But this shouldn't occur unless it's an accident
  if (newStartTime === endTime && !allowZeroTime) {
    newStartTime = null;
    newEndTime = null;
  }

  return {
    [startTimeKey]: newStartTime,
    [endTimeKey]: newEndTime,
  };
};

const createManualEntryTask = ({
  date,
  values,
  timezone,
  hourBased,
  currentType,
  selectedEntryHourBased,
  entryDate,
  selectedEntryStartTime,
  selectedEntryEndTime,
  shouldRoundTimes,
  manualEntryRoundingInterval,
  manualEntryRoundingSetting,
  preparedResponses,
  hasPaidFlag,
  showOvertime,
  showDoubleOvertime,
  showBreak,
  showPTO,
  isFutureDate,
  useEndDateOvernight,
  originalStart,
  originalEnd,
  showOriginalTimes = false,
  days,
}) => {
  const {
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    startTime,
    endTime = null,
    ptoStartTime,
    ptoEndTime,
    users: taskUsers,
    ...restValues
  } = values;

  const startOfDay = DateTime.fromSQL(date, { zone: timezone }).startOf('day').toMillis();
  // We need to offset the end time by the number of days that we have iterated through
  // When we are in the first day, the offset is 0
  const dayOffset = days * 24 * TaskHelpers.SECONDS_IN_HOUR * TaskHelpers.MILLISECONDS_IN_SECOND;

  const hasEndTime = !isNullOrUndefined(endTime);

  const ourStart = startTime + dayOffset;
  let ourEnd = hasEndTime ? endTime + dayOffset : null;

  let offset = 0;

  if (hourBased && selectedEntryHourBased && date === entryDate && hasEndTime) {
    /*
      Hour based entry values only has endTime
      Prevent overwriting of startTime for existing entries

      endTime comes back as referenced from startOfDay
      so we shift it by order offset to keep duration
    */
    offset = (
      (selectedEntryStartTime % (60 * 1000))
    );
    ourEnd = endTime + offset;
  }

  const shouldRound = shouldRoundTimes && manualEntryRoundingInterval;

  const regularTask = TaskHelpers.shiftOvernightTimes({
    ...restValues,
    date,
    startTime: ourStart,
    endTime: ourEnd,
    originalStart,
    originalEnd,
    hourBased,
    customData: preparedResponses,
    canEdit: true,
    timezone,
    ...getTimes({
      startTime: ourStart,
      endTime: ourEnd,
      startTimeKey: 'startTime',
      endTimeKey: 'endTime',
      allowZeroTime: true,
      timezone,
    }),
  }, !useEndDateOvernight);

  const hasWork = currentType === 'work' || hasPaidFlag;

  /*
    https://projectharbour.atlassian.net/browse/HARBOUR-5326
    real time entries have seconds + milliseconds while manual only have minutes
    need to strip these from the timestamp
  */

  if (!hourBased && hasWork) {
    if (showOriginalTimes) {
      // eslint-disable-next-line max-len
      if (stripSecondsAndMilliseconds(regularTask.startTime) !== stripSecondsAndMilliseconds(originalStart)
        // eslint-disable-next-line max-len
        || stripSecondsAndMilliseconds(regularTask.endTime) !== stripSecondsAndMilliseconds(originalEnd)
      ) {
        // User changed the start/end, update the original times
        regularTask.originalStart = regularTask.startTime;
        regularTask.originalEnd = regularTask.endTime;
      } else {
        // We preload the start/end with the original times,
        // So we need to reset
        regularTask.startTime = selectedEntryStartTime;
        regularTask.endTime = selectedEntryEndTime;
      }
    } else {
      regularTask.originalStart = regularTask.startTime;
      regularTask.originalEnd = regularTask.endTime;
    }
  }

  const tasks = {
    task: regularTask,
  };

  const roundedTimes = TaskHelpers.getRoundedTime({
    ...regularTask,
    startTimeKey: 'startTime',
    endTimeKey: 'endTime',
    roundingInterval: manualEntryRoundingInterval,
    shouldRound,
    keepOriginalTimes: true,
    roundingSetting: manualEntryRoundingSetting,
  });

  tasks.task = {
    ...regularTask,
    ...roundedTimes,
  };

  if (hasPaidFlag) {
    // Shift overnight times
    const shiftedTask = TaskHelpers.shiftOvernightTimes({
      ...tasks.task,
      type: currentType ?? 'work',
      ...getTimes({
        startTime: otStartTime + dayOffset,
        endTime: otEndTime + dayOffset,
        startTimeKey: 'otStartTime',
        endTimeKey: 'otEndTime',
        isTimeShown: showOvertime,
        timezone,
      }),
      ...getTimes({
        startTime: doubleOTStartTime + dayOffset,
        endTime: doubleOTEndTime + dayOffset,
        startTimeKey: 'doubleOTStartTime',
        endTimeKey: 'doubleOTEndTime',
        isTimeShown: showDoubleOvertime,
        timezone,
      }),
      ...getTimes({
        startTime: breakStartTime + dayOffset,
        endTime: breakEndTime + dayOffset,
        startTimeKey: 'breakStartTime',
        endTimeKey: 'breakEndTime',
        isTimeShown: showBreak,
        timezone,
      }),
      ...getTimes({
        startTime: ptoStartTime + dayOffset,
        endTime: ptoEndTime + dayOffset,
        startTimeKey: 'ptoStartTime',
        endTimeKey: 'ptoEndTime',
        isTimeShown: (showPTO || isFutureDate) && hourBased,
        allowZeroTime: true,
        timezone,
      }),
    }, !useEndDateOvernight);

    const roundedOvertime = TaskHelpers.getRoundedTime({
      startTime: shiftedTask.otStartTime,
      endTime: shiftedTask.otEndTime,
      startTimeKey: 'otStartTime',
      endTimeKey: 'otEndTime',
      roundingInterval: manualEntryRoundingInterval,
      shouldRound,
      roundingSetting: manualEntryRoundingSetting,
    });

    const roundedDoubleOT = TaskHelpers.getRoundedTime({
      startTime: shiftedTask.doubleOTStartTime,
      endTime: shiftedTask.doubleOTEndTime,
      startTimeKey: 'doubleOTStartTime',
      endTimeKey: 'doubleOTEndTime',
      roundingInterval: manualEntryRoundingInterval,
      shouldRound,
      roundingSetting: manualEntryRoundingSetting,
    });

    const roundedBreak = TaskHelpers.getRoundedTime({
      startTime: shiftedTask.breakStartTime,
      endTime: shiftedTask.breakEndTime,
      startTimeKey: 'breakStartTime',
      endTimeKey: 'breakEndTime',
      roundingInterval: manualEntryRoundingInterval,
      shouldRound,
      roundingSetting: manualEntryRoundingSetting,
    });

    const roundedPTO = TaskHelpers.getRoundedTime({
      startTime: shiftedTask.ptoStartTime,
      endTime: shiftedTask.ptoEndTime,
      startTimeKey: 'ptoStartTime',
      endTimeKey: 'ptoEndTime',
      roundingInterval: manualEntryRoundingInterval,
      shouldRound,
      roundingSetting: manualEntryRoundingSetting,
    });

    tasks.task = {
      ...shiftedTask,
      ...roundedOvertime,
      ...roundedDoubleOT,
      ...roundedBreak,
      ...roundedPTO,
    };

    if (
      !startTime
      && !endTime
      && !breakStartTime
      && !breakEndTime
      && !otStartTime
      && !otEndTime
      && !doubleOTStartTime
      && !doubleOTEndTime
      && !ptoStartTime
      && !ptoEndTime
    ) {
      tasks.task = {
        ...shiftedTask,
        startTime: startOfDay,
        endTime: startOfDay,
      };
    }
  }

  if (showBreak && !hasPaidFlag) {
    const hasData = ((hourBased || breakStartTime) && breakEndTime);
    if (hasData) {
      let breakStart = breakStartTime;
      if (!breakStart) {
        breakStart = calculateStartTime(breakEndTime, timezone);
      }
      const breakTask = TaskHelpers.shiftOvernightTimes({
        ...restValues,
        type: 'break',
        date,
        startTime: breakStart,
        endTime: breakEndTime,
        hourBased,
        customData: preparedResponses,
        canEdit: true,
        timezone,
      }, !useEndDateOvernight);

      const roundedBreakTimes = TaskHelpers.getRoundedTime({
        ...breakTask,
        startTimeKey: 'startTime',
        endTimeKey: 'endTime',
        roundingInterval: manualEntryRoundingInterval,
        shouldRound,
        roundingSetting: manualEntryRoundingSetting,
      });

      tasks.breakObject = {
        ...breakTask,
        ...roundedBreakTimes,
      };
    }
  }

  if (showOvertime && !hasPaidFlag) {
    const hasData = ((hourBased || otStartTime) && otEndTime);
    if (hasData) {
      let otStart = otStartTime;
      if (!otStart) {
        otStart = calculateStartTime(otEndTime, timezone);
      }
      const overtimeTask = TaskHelpers.shiftOvernightTimes({
        ...restValues,
        type: 'overtime',
        date,
        startTime: otStart,
        endTime: otEndTime,
        hourBased,
        customData: preparedResponses,
        canEdit: true,
        timezone,
      }, !useEndDateOvernight);

      const roundedOvertimeTimes = TaskHelpers.getRoundedTime({
        ...overtimeTask,
        startTimeKey: 'startTime',
        endTimeKey: 'endTime',
        roundingInterval: manualEntryRoundingInterval,
        shouldRound,
        roundingSetting: manualEntryRoundingSetting,
      });

      tasks.overtime = {
        ...overtimeTask,
        ...roundedOvertimeTimes,
      };
    }
  }

  if (showPTO && !hasPaidFlag) {
    const hasData = ((hourBased || ptoStartTime) && ptoEndTime);
    if (hasData) {
      let ptoStart = ptoStartTime;
      if (!ptoStart) {
        ptoStart = calculateStartTime(ptoEndTime, timezone);
      }
      const ptoTask = TaskHelpers.shiftOvernightTimes({
        ...restValues,
        type: 'pto',
        date,
        startTime: ptoStart,
        endTime: ptoEndTime,
        hourBased,
        customData: preparedResponses,
        canEdit: true,
        timezone,
      }, !useEndDateOvernight);

      const roundedPTOTimes = TaskHelpers.getRoundedTime({
        ...ptoTask,
        startTimeKey: 'startTime',
        endTimeKey: 'endTime',
        roundingInterval: manualEntryRoundingInterval,
        shouldRound,
        roundingSetting: manualEntryRoundingSetting,
      });

      tasks.pto = {
        ...ptoTask,
        ...roundedPTOTimes,
      };
    }
  }

  // Hidden OT/DT rows are unmounted, so leftover segments from the original
  // entry must be cleared or addOrUpdateEntries keeps them via selectedEntry.
  if (!showOvertime) {
    tasks.task.otStartTime = null;
    tasks.task.otEndTime = null;
  }
  if (!showDoubleOvertime) {
    tasks.task.doubleOTStartTime = null;
    tasks.task.doubleOTEndTime = null;
  }

  tasks.users = taskUsers;
  return tasks;
};

export const createManualEntryTasks = ({
  values,
  timezone,
  hourBased,
  currentType,
  selectedEntryHourBased,
  entryDate,
  selectedEntryStartTime,
  selectedEntryEndTime,
  shouldRoundTimes,
  manualEntryRoundingInterval,
  manualEntryRoundingSetting,
  preparedResponses,
  hasPaidFlag,
  showOvertime,
  showDoubleOvertime,
  showBreak,
  showPTO,
  isFutureDate,
  useEndDateOvernight,
  originalStart,
  originalEnd,
  showOriginalTimes = false,
  isMultiDayEntry,
}) => {
  const {
    date,
    dateRange,
  } = values;

  const unparsedStart = isMultiDayEntry ? dateRange?.[0] : date;
  const unparsedEnd = isMultiDayEntry ? dateRange?.[1] : date;

  if (!unparsedStart || !unparsedEnd) return [];

  const start = moment(unparsedStart);
  const end = moment(unparsedEnd).add(1, 'day');

  const tasks = [];
  let days = 0;

  while (start.isBefore(end)) {
    tasks.push(createManualEntryTask({
      date: start.format('YYYY-MM-DD'),
      values,
      timezone,
      hourBased,
      currentType,
      selectedEntryHourBased,
      entryDate: isMultiDayEntry ? start.format('YYYY-MM-DD') : entryDate,
      selectedEntryStartTime,
      selectedEntryEndTime,
      shouldRoundTimes,
      manualEntryRoundingInterval,
      manualEntryRoundingSetting,
      preparedResponses,
      hasPaidFlag,
      showOvertime,
      showDoubleOvertime,
      showBreak,
      showPTO,
      isFutureDate,
      useEndDateOvernight,
      originalStart,
      originalEnd,
      showOriginalTimes,
      days,
    }));
    start.add(1, 'day');
    days += 1;
  }

  return tasks;
};

// Only checks for bad times when paid flag is true
export const timesAreBad = ({
  hourBased,
  startTime,
  endTime,
  breakStartTime,
  breakEndTime,
  otStartTime,
  otEndTime,
  doubleOTStartTime,
  doubleOTEndTime,
  ptoStartTime,
  ptoEndTime,
  showBreak,
  showOvertime,
  showDoubleOvertime,
  showPTO,
  currentType,
}) => {
  const isWork = currentType === 'work';
  const isPTO = currentType === 'pto';

  const badBreak = (
    (hourBased && (isNullOrUndefined(breakEndTime)) && showBreak)
    || (
      !hourBased && showBreak && (
        !breakStartTime
        || !breakEndTime
      )
    )
  );
  const badTime = (
    (hourBased && (isNullOrUndefined(endTime)) && isWork)
    || (
      !hourBased && isWork && (
        (!startTime && endTime)
        || (startTime && !endTime)
      )
    )
  );
  const badOT = (
    (hourBased && (isNullOrUndefined(otEndTime)) && showOvertime)
    || (
      !hourBased && showOvertime && (
        !otStartTime
        || !otEndTime
      )
    )
  );
  const badDoubleOT = (
    (hourBased && (isNullOrUndefined(doubleOTEndTime)) && showDoubleOvertime)
    || (
      !hourBased && showDoubleOvertime && (
        !doubleOTStartTime
        || !doubleOTEndTime
      )
    )
  );

  const badPTO = (
    (hourBased && (isNullOrUndefined(ptoEndTime)) && (showPTO || isPTO))
    || (
      !hourBased && (showPTO || isPTO) && (
        !ptoStartTime
        || !ptoEndTime
      )
    )
  );

  // If no values filled out
  if (
    hourBased
    && !breakEndTime
    && isNullOrUndefined(endTime)
    && !otEndTime
    && !doubleOTEndTime
    && isNullOrUndefined(ptoEndTime)
  ) {
    return {
      badBreak: true,
      badTime: true,
      badOT: true,
      badDoubleOT: true,
      badPTO: true,
    };
  }

  if (
    hourBased
    && (
      badBreak
      || badOT
      || badDoubleOT
      || (isWork && badPTO)
    )
  ) {
    return {
      badBreak: true,
      badTime: true,
      badOT: true,
      badDoubleOT: true,
      badPTO: true,
    };
  }

  // If no values filled out
  if (
    !hourBased
    && !breakStartTime
    && !breakEndTime
    && !startTime
    && !endTime
    && !otStartTime
    && !otEndTime
    && !doubleOTStartTime
    && !doubleOTEndTime
    && !ptoStartTime
    && !ptoEndTime
  ) {
    return {
      badBreak: true,
      badTime: true,
      badOT: true,
      badDoubleOT: true,
      badPTO: true,
    };
  }

  // If values filled out but one are bad
  if (
    !hourBased
    && (
      badBreak
      || badTime
      || badOT
      || badDoubleOT
      || badPTO
    )
  ) {
    return {
      badBreak: true,
      badTime: true,
      badOT: true,
      badDoubleOT: true,
      badPTO: true,
    };
  }

  return {
    badBreak,
    badTime,
    badOT,
    badDoubleOT,
    badPTO,
  };
};

export const getTimesFromHoursMinutes = ({
  date,
  timezone,
  hoursMinutes,
  startTime,
  endTime,
  startTimeKey,
  endTimeKey,
  // Hour-based form values often only have hours/minutes; pass the
  // selected entry startTime so we can keep its ordering remainder.
  orderOffsetSource,
}) => {
  if (!hoursMinutes) return { [startTimeKey]: startTime, [endTimeKey]: endTime };

  // Round to whole minutes so leftover seconds don't become fractional
  // minutes (Luxon set() truncates them, minus() does not).
  const totalMinutes = Math.round(
    (hoursMinutes.hours || 0) * 60 + (hoursMinutes.minutes || 0),
  );
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Preserve sub-minute remainder used for hour-based entry ordering.
  const offsetSource = startTime || orderOffsetSource;
  const orderOffset = offsetSource ? offsetSource % (60 * 1000) : 0;

  // Web hour-based: set end from wall-clock hours/minutes, then subtract
  // to get start. Using plus() would break DST (e.g. 4h from midnight on
  // spring-forward lands at 5AM and displays as 5 hours).
  const startOfDay = DateTime.fromISO(date, { zone: timezone }).startOf('day');

  const newEndTime = startOfDay
    .set({ hours, minutes })
    .plus({ milliseconds: orderOffset });
  const newStartTime = newEndTime.minus({ hours, minutes });

  return {
    [startTimeKey]: newStartTime.toMillis(),
    [endTimeKey]: newEndTime.toMillis(),
  };
}
// Helper function to check if a time is at local midnight (12:00 AM)
const isMidnight = (ms) => {
  if (isNullOrUndefined(ms)) return false;

  const dt = DateTime.fromMillis(ms);
  return dt.hour === 0 && dt.minute === 0;
};

// Helper function to check if a time duration is zero
const isZeroDuration = (startTime, endTime, hourBased) => {
  if (isNullOrUndefined(endTime) || (!hourBased && isNullOrUndefined(startTime))) return true;

  if (hourBased) {
    return isMidnight(endTime);
  } else {
    const duration = endTime - startTime;
    return duration <= 0;
  }
};

// Helper function to check if a time duration would be zero after rounding
const wouldRoundToZero = (
  startTime,
  endTime,
  hourBased,
  shouldRound,
  manualEntryRoundingInterval,
  manualEntryRoundingSetting,
  timezone,
) => {
  if (isNullOrUndefined(endTime) || (!hourBased && isNullOrUndefined(startTime))) return true;

  if (!shouldRound) {
    return isZeroDuration(startTime, endTime, hourBased);
  }

  const actualStartTime = hourBased && !startTime
    ? calculateStartTime(endTime, timezone || DateTime.local().zoneName)
    : startTime;

  const roundedTimes = TaskHelpers.getRoundedTime({
    startTime: actualStartTime,
    endTime,
    startTimeKey: 'startTime',
    endTimeKey: 'endTime',
    roundingInterval: manualEntryRoundingInterval,
    shouldRound,
    roundingSetting: manualEntryRoundingSetting,
  });

  const {
    startTime: roundedStart,
    endTime: roundedEnd,
  } = roundedTimes;

  if (isNullOrUndefined(roundedEnd) || isNullOrUndefined(roundedStart)) return true;

  const duration = roundedEnd - roundedStart;
  return duration <= 0;
};

/**
 * Checks if the time entry is valid when non-zero time entries are enabled
 * Returns true if any visible time entry (regular time, overtime, double overtime, break, or PTO)
 * is non-zero, false if all visible time entries are zero.
 * @param {Object} values - The values of the time entry
 * @param {boolean} hourBased - Whether the time entry is hour based
 * @param {string} currentType - The type of the time entry
 * @param {boolean} isFutureDate - Whether the time entry is a future date
 * @param {boolean} hasPTOPermission - Whether the user has PTO permission
 * @param {boolean} showBreak - Whether break time is shown
 * @param {boolean} showOvertime - Whether overtime is shown
 * @param {boolean} showDoubleOvertime - Whether double overtime is shown
 * @param {boolean} showPTO - Whether PTO is shown
 * @param {boolean} shouldRoundTimes - Whether rounding is enabled
 * @param {number} manualEntryRoundingInterval - The rounding interval in minutes
 * @param {string} manualEntryRoundingSetting - The rounding setting (automatic/roundUp/roundDown)
 * @param {string} timezone - The timezone for the entry
 */
export const isNonZeroTimeEntryValid = ({
  values,
  hourBased,
  currentType,
  isFutureDate,
  hasPTOPermission,
  showBreak,
  showOvertime,
  showDoubleOvertime,
  showPTO,
  shouldRoundTimes = false,
  manualEntryRoundingInterval = null,
  manualEntryRoundingSetting = null,
  timezone = null,
}) => {
  const {
    startTime,
    endTime,
    breakStartTime,
    breakEndTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
  } = values;
  const durationChecks = [];

  const shouldRound = shouldRoundTimes && manualEntryRoundingInterval;

  if (currentType === 'pto' && isFutureDate && hasPTOPermission) {
    durationChecks.push(isZeroDuration(ptoStartTime, ptoEndTime, hourBased));
  } else {
    durationChecks.push(wouldRoundToZero(
      startTime,
      endTime,
      hourBased,
      shouldRound,
      manualEntryRoundingInterval,
      manualEntryRoundingSetting,
      timezone,
    ));
  }

  if (showBreak && currentType !== 'break') {
    durationChecks.push(wouldRoundToZero(
      breakStartTime,
      breakEndTime,
      hourBased,
      shouldRound,
      manualEntryRoundingInterval,
      manualEntryRoundingSetting,
      timezone,
    ));
  }
  if (showOvertime && currentType !== 'overtime') {
    durationChecks.push(wouldRoundToZero(
      otStartTime,
      otEndTime,
      hourBased,
      shouldRound,
      manualEntryRoundingInterval,
      manualEntryRoundingSetting,
      timezone,
    ));
  }
  if (showDoubleOvertime) {
    durationChecks.push(wouldRoundToZero(
      doubleOTStartTime,
      doubleOTEndTime,
      hourBased,
      shouldRound,
      manualEntryRoundingInterval,
      manualEntryRoundingSetting,
      timezone,
    ));
  }
  if (showPTO && currentType !== 'pto') {
    durationChecks.push(isZeroDuration(ptoStartTime, ptoEndTime, hourBased));
  }

  const hasAnyNonZero = durationChecks.some((isZero) => !isZero);
  return hasAnyNonZero;
};
